import { useState } from 'react';

import { GlobalScrollLock } from 'components/GlobalScrollLock';

import { SHARE_COPY_FEEDBACK_MS } from '../constants';
import { buildShareMessage, buildWhatsAppShareUrl } from '../helpers';
import * as styled from '../styles/share-modal';

type Props = {
  providerName: string;
  profileUrl: string;
  onClose: () => void;
};

const hasNativeShare = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof (navigator as Navigator & { share?: unknown }).share === 'function';

export const ShareModal = ({ providerName, profileUrl, onClose }: Props) => {
  const [message, setMessage] = useState(() =>
    buildShareMessage(providerName, profileUrl),
  );
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!navigator?.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(message)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), SHARE_COPY_FEEDBACK_MS);
      })
      .catch(() => null);
  };

  const handleWhatsApp = () => {
    window.open(
      buildWhatsAppShareUrl(message),
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleNativeShare = () => {
    const nativeShare = (
      navigator as Navigator & { share?: (data: ShareData) => Promise<void> }
    ).share;
    if (!nativeShare) return;
    nativeShare({ text: message, url: profileUrl }).catch(() => null);
  };

  return (
    <styled.Overlay role="dialog" aria-modal="true">
      <GlobalScrollLock />
      <styled.Sheet>
        <styled.Header>
          <styled.HeaderText>
            <styled.Title>Loved {providerName}?</styled.Title>
            <styled.Subtitle>
              Share their profile with a friend who'd love their work.
            </styled.Subtitle>
          </styled.HeaderText>
          <styled.CloseBtn
            type="button"
            onClick={onClose}
            aria-label="Close share modal"
          >
            ✕
          </styled.CloseBtn>
        </styled.Header>

        <styled.HelperText>
          We've drafted a message — edit it however you like.
        </styled.HelperText>
        <styled.MessageInput
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={5}
          aria-label="Share message"
        />

        <styled.Actions>
          <styled.PrimaryBtn type="button" onClick={handleCopy}>
            {copied ? '✓ Copied' : 'Copy message'}
          </styled.PrimaryBtn>
          <styled.SecondaryBtn type="button" onClick={handleWhatsApp}>
            Share on WhatsApp
          </styled.SecondaryBtn>
          {hasNativeShare() && (
            <styled.SecondaryBtn type="button" onClick={handleNativeShare}>
              More apps…
            </styled.SecondaryBtn>
          )}
        </styled.Actions>

        <styled.DismissRow>
          <styled.DismissBtn type="button" onClick={onClose}>
            Maybe later
          </styled.DismissBtn>
        </styled.DismissRow>
      </styled.Sheet>
    </styled.Overlay>
  );
};
